export const COMPOSER_MAX_HEIGHT_PX = 120;
export const KEYBOARD_SHORTCUT_KEY = "k";

/** Draggable FAB geometry. */
export const FAB_SIZE_PX = 60;
export const FAB_MARGIN_PX = 16;
/** Pointer travel before a press becomes a drag (vs a tap that opens Maya). */
export const FAB_DRAG_THRESHOLD_PX = 4;
/** Resizable FAB bounds (diameter). FAB_SIZE_PX is the default. */
export const FAB_MIN_SIZE_PX = 48;
export const FAB_MAX_SIZE_PX = 72;

/** DOM id of the chat sidebar — the launcher's `aria-controls` target. */
export const MAYA_SIDEBAR_ID = "maya-sidebar";
/** DOM id of the chat switcher — the header toggle's `aria-controls`. */
export const MAYA_CHATS_VIEW_ID = "maya-chats";

/** Docked chat sidebar width (desktop). Height always fills the layout row. */
export const SIDEBAR_DEFAULT_WIDTH_PX = 380;
export const SIDEBAR_MIN_WIDTH_PX = 320;
export const SIDEBAR_MAX_WIDTH_PX = 640;
/** Never let the sidebar squeeze the page it is talking about below this. */
export const SIDEBAR_MIN_PAGE_WIDTH_PX = 480;
/** Overlay drawer width below the desktop breakpoint (capped by the viewport). */
export const SIDEBAR_OVERLAY_WIDTH_PX = 420;

/**
 * Query params that say which tab or record of a page is open — the only part
 * of the query string sent as context. The rest (search text, filters, or
 * anything a crafted link carries) stays out of the prompt. Mirrors
 * NAVIGATION_PARAMS in the API's page-context-prompt.util.ts, which enforces it.
 */
export const PAGE_CONTEXT_PARAMS = [
  "hTab",
  "vTab",
  "sTab",
  "tab",
  "edit",
  "branch",
  "country",
  "group",
] as const;

/** Server-side caps on the chat request's `pageContext` (AiPageContextDto). */
export const PAGE_CONTEXT_LIMITS = {
  path: 500,
  title: 200,
  breadcrumbs: 10,
  breadcrumb: 100,
} as const;

export const INITIAL_GREETING_KEY = "common:maya.initial_greeting";

export const CURSOR_TRACKING = {
  // Applied to the inlined SVG face, so offsets are in the artwork's user units
  // (viewBox 694×633), not CSS px. 24 reads as a glance without visibly pushing
  // the eyes off the visor's centre line at the FAB's ~44px render size (40 was
  // roughly half an eye width and looked like a misaligned visor). Head tilt is
  // a rotation → scale-invariant.
  maxPupilOffset: 24,
  maxHeadTilt: 6,
  // Cursor distance (CSS px) at which deflection saturates. Below it the glance
  // scales with distance, so a pointer resting near/over the FAB relaxes to
  // neutral instead of staying pinned at full deflection.
  reach: 420,
  smoothing: 0.18,
} as const;

export const STORAGE_KEYS = {
  /** The open chat — messages, session and history cursor in ONE record
   *  (sessionStorage), so a partial write can never pair one chat's
   *  transcript with another chat's session. One per space (see spaces.ts). */
  chat: "maya:chat",
  companyChat: "maya:chat:company",
  /** Pre-`chat` keys, read once on upgrade and then removed. */
  legacySessionId: "maya:sessionId",
  legacyMessages: "maya:messages",
  scopedUser: "maya:userId",
  /** The unsent message, one per space. */
  draft: "maya:draft",
  companyDraft: "maya:draft:company",
  /** Persisted FAB position (localStorage, long-lived). */
  fabPosition: "maya:fabPos",
  /** Persisted FAB diameter (localStorage). */
  fabSize: "maya:fabSize",
  /** Persisted sidebar width (localStorage). */
  sidebarWidth: "maya:sidebarWidth",
  /** Whether the sidebar is open — survives a reload in this tab (sessionStorage). */
  sidebarOpen: "maya:sidebarOpen",
  /** Whether turns carry the open page as context (localStorage, default on). */
  sharePageContext: "maya:sharePageContext",
} as const;

/** The "Looking at" lens, with its switch for leaving the open page out of
 *  the chat, is hidden for now, so the page is always shared. `true` brings
 *  it back, with each browser's earlier choice intact. */
export const PAGE_LENS_ENABLED = false;

/** Gradients the Maya components paint inline — built from brand tokens. */
export const PALETTE = {
  userBubble:
    "linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-strong) 100%)",
  auraGlow:
    "radial-gradient(circle, color-mix(in oklab, var(--color-primary) 45%, transparent) 0%, color-mix(in oklab, var(--color-primary) 18%, transparent) 45%, transparent 70%)",
} as const;
